import { useCallback, useEffect, useRef, useState } from 'react';
import { Delete } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Avatar } from '@/components/ui/Avatar';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S1 — Lock Screen (PIN unlock)

   Full-screen overlay. Large numpad designed for gloved fingers (min 64px).
   4-digit PIN with dot indicators, shake on wrong PIN.
   ========================================================================== */

const PIN_LENGTH = 4;

const NUM_KEYS = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['', '0', 'del'],
] as const;

export function LockScreen() {
  const { user, verifyPin, pinAttemptsRemaining, logout } = useAuth();
  const [pin, setPin] = useState('');
  const [shaking, setShaking] = useState(false);
  const [checking, setChecking] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Full name for avatar
  const fullName = user ? `${user.nome} ${user.cognome}` : 'Operatrice';

  // ------------------------------------------------------------------
  // Auto-submit when 4 digits entered
  // ------------------------------------------------------------------

  useEffect(() => {
    if (pin.length !== PIN_LENGTH) return;

    const verify = async () => {
      setChecking(true);
      const success = await verifyPin(pin);
      if (!success) {
        setShaking(true);
        // Reset after shake animation
        setTimeout(() => {
          setShaking(false);
          setPin('');
          setChecking(false);
        }, 550);
      } else {
        setChecking(false);
      }
    };

    verify();
  }, [pin, verifyPin]);

  // ------------------------------------------------------------------
  // Keyboard support
  // ------------------------------------------------------------------

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (checking) return;
      if (/^[0-9]$/.test(e.key) && pin.length < PIN_LENGTH) {
        setPin((p) => p + e.key);
      } else if (e.key === 'Backspace') {
        setPin((p) => p.slice(0, -1));
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [pin, checking]);

  // ------------------------------------------------------------------
  // Numpad press handler
  // ------------------------------------------------------------------

  const handleKey = useCallback(
    (key: string) => {
      if (checking) return;
      if (key === 'del') {
        setPin((p) => p.slice(0, -1));
      } else if (key && pin.length < PIN_LENGTH) {
        setPin((p) => p + key);
      }
    },
    [pin, checking],
  );

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------

  return (
    <div
      ref={containerRef}
      className={cn(
        'fixed inset-0 z-[var(--z-modal)]',
        'flex flex-col items-center justify-center',
        'bg-surface-base',
        'px-6',
        'select-none',
      )}
    >
      {/* Avatar + name */}
      <div className="mb-8 flex flex-col items-center gap-3">
        <Avatar name={fullName} size="xl" />
        <p className="font-[family-name:var(--font-heading)] text-xl font-bold text-text-primary">
          {user?.nome ?? 'Operatrice'}
        </p>
        <p className="text-sm text-text-muted">Inserisci il PIN per continuare</p>
      </div>

      {/* PIN dots */}
      <div
        className={cn(
          'mb-2 flex items-center justify-center gap-4',
          shaking && 'shake',
        )}
        aria-label={`PIN: ${pin.length} di ${PIN_LENGTH} cifre inserite`}
      >
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-4 w-4 rounded-full',
              'transition-all duration-[var(--duration-fast)] ease-[var(--ease-out)]',
              i < pin.length
                ? 'bg-accent-primary scale-110'
                : 'bg-border-default',
            )}
          />
        ))}
      </div>

      {/* Attempts remaining warning */}
      <div className="mb-8 h-6 flex items-center justify-center">
        {pinAttemptsRemaining < 5 && pinAttemptsRemaining > 0 && (
          <p className="text-sm text-status-negative fade-in">
            {pinAttemptsRemaining === 1
              ? '1 tentativo rimanente'
              : `${pinAttemptsRemaining} tentativi rimanenti`}
          </p>
        )}
      </div>

      {/* Numpad */}
      <div className="grid grid-cols-3 gap-3 w-full max-w-xs">
        {NUM_KEYS.flat().map((key, i) => {
          if (key === '') {
            return <div key={i} />;
          }

          if (key === 'del') {
            return (
              <button
                key={i}
                onClick={() => handleKey('del')}
                disabled={pin.length === 0 || checking}
                className={cn(
                  'flex items-center justify-center',
                  'h-[var(--touch-large)] rounded-[var(--radius-lg)]',
                  'text-text-secondary',
                  'transition-all duration-[var(--duration-fast)]',
                  'hover:bg-surface-sunken active:bg-border-default',
                  'disabled:opacity-30 disabled:pointer-events-none',
                  'cursor-pointer',
                )}
                aria-label="Cancella"
              >
                <Delete className="h-6 w-6" strokeWidth={1.5} />
              </button>
            );
          }

          return (
            <button
              key={i}
              onClick={() => handleKey(key)}
              disabled={pin.length >= PIN_LENGTH || checking}
              className={cn(
                'flex items-center justify-center',
                'h-[var(--touch-large)] rounded-[var(--radius-lg)]',
                'bg-surface-card border border-border-default',
                'font-[family-name:var(--font-heading)] text-2xl font-semibold text-text-primary',
                'shadow-xs',
                'transition-all duration-[var(--duration-fast)]',
                'hover:bg-surface-sunken hover:shadow-sm',
                'active:scale-95 active:bg-border-default',
                'disabled:opacity-40 disabled:pointer-events-none',
                'cursor-pointer',
              )}
            >
              {key}
            </button>
          );
        })}
      </div>

      {/* Change user link */}
      <button
        onClick={logout}
        className={cn(
          'mt-10 text-sm text-text-muted',
          'hover:text-accent-primary',
          'transition-colors cursor-pointer',
          'underline underline-offset-2 decoration-text-muted/40',
          'hover:decoration-accent-primary/60',
        )}
      >
        Cambia utente
      </button>
    </div>
  );
}
